/**
 * AssetsService.ts
 * Service de gestion des assets avec Supabase Storage
 * Remplace les uploads vers le backend Express par Supabase Storage
 */
import { supabase, isSupabaseConfigured } from './SupabaseService';
import type { Database } from '../types/supabase';
import { logger } from '../utils/ProfessionalLogger';
import { buildUploadPath, sanitizeFilename, generateUuidFilename } from '../utils/storagePaths';
// FIX runtime : import statique (require() casse en ESM/Vite → "require is not defined").
import { formatStoragePathAsUrl } from './StorageUrlService';

// Types pour les assets
export type AssetRow = Database['public']['Tables']['template_editor_assets']['Row'];
export type AssetInsert = Database['public']['Tables']['template_editor_assets']['Insert'];

/**
 * Options pour l'upload d'un asset
 */
export interface UploadAssetOptions {
  file: File;
  projectId?: string;
  bucket?: 'user-uploads' | 'user-assets' | 'thumbnails';
}

/**
 * Résultat d'un upload d'asset
 */
export interface UploadAssetResult {
  url: string;
  path: string;
  metadata: AssetRow;
}

/**
 * Résultat d'une opération sur un asset
 */
export interface AssetOperationResult<T = UploadAssetResult> {
  data: T | null;
  error: Error | null;
}

/**
 * Service de gestion des assets avec Supabase Storage
 */
export class AssetsService {
  private readonly DEFAULT_BUCKET = 'user-uploads';

  /**
   * Upload un fichier vers Supabase Storage
   */
  async uploadAsset(options: UploadAssetOptions): Promise<AssetOperationResult<UploadAssetResult>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn("Tentative d'upload d'asset sans configuration Supabase");
      return { data: null, error };
    }

    try {
      // Récupérer l'utilisateur actuel
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        logger.error("Impossible de récupérer l'utilisateur actuel:", userError);
        return { data: null, error: new Error('Utilisateur non authentifié') };
      }

      const bucket = options.bucket || this.DEFAULT_BUCKET;

      // PHASE 2: Utiliser les helpers standardisés pour générer le chemin
      // Nettoyer et générer un nom de fichier unique
      const fileExtension = options.file.name.split('.').pop() || 'bin';
      const _sanitizedOriginal = sanitizeFilename(options.file.name);
      const uniqueFilename = generateUuidFilename(fileExtension);

      // Déterminer le chemin de stockage avec format standardisé
      // Format: {userId}/{projectId}/uploads/{filename} ou {userId}/{filename}
      let storagePath: string;
      if (options.projectId) {
        storagePath = buildUploadPath(user.id, options.projectId, uniqueFilename);
      } else {
        // Pour les assets réutilisables sans projet, utiliser le format simple
        storagePath = `${user.id}/${uniqueFilename}`;
      }

      // Upload vers Supabase Storage
      const { data: _uploadData, error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(storagePath, options.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        logger.error("Erreur lors de l'upload vers Supabase Storage:", uploadError);
        return { data: null, error: new Error(uploadError.message) };
      }

      // PHASE 2: Le bucket est privé (public=false), donc on stocke storage_path
      // La signed URL sera générée à la demande avec cache via StorageUrlService
      // Insérer les métadonnées dans la table assets
      const assetData: AssetInsert = {
        user_id: user.id,
        project_id: options.projectId || null,
        storage_path: storagePath,
        filename: options.file.name,
        size_bytes: options.file.size,
        mime_type: options.file.type,
      };

      const { data: metadataData, error: metadataError } = await supabase
        .from('template_editor_assets')
        .insert(assetData)
        .select()
        .single();

      if (metadataError) {
        logger.warn("Erreur lors de l'insertion des métadonnées de l'asset:", metadataError);
        // Ne pas bloquer si les métadonnées échouent, l'upload est quand même réussi
      }

      logger.info(`✅ Asset uploadé: ${storagePath} (${this.formatFileSize(options.file.size)})`);

      // PHASE 2: Retourner storage_path formaté au lieu de public URL
      // Utiliser StorageUrlService.ensureSignedUrl() pour obtenir une signed URL à la demande
      const { formatStoragePathAsUrl } = await import('./StorageUrlService');
      const storagePathUrl = formatStoragePathAsUrl(storagePath, bucket);

      return {
        data: {
          url: storagePathUrl, // Format: supabase://bucket/storage-path
          path: storagePath,
          metadata: metadataData as AssetRow,
        },
        error: null,
      };
    } catch (error) {
      logger.error("Exception lors de l'upload de l'asset:", error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Supprime un asset de Supabase Storage
   */
  async deleteAsset(
    storagePath: string,
    bucket: string = this.DEFAULT_BUCKET
  ): Promise<AssetOperationResult<void>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn("Tentative de suppression d'asset sans configuration Supabase");
      return { data: null, error };
    }

    try {
      // Supprimer de Supabase Storage
      const { error: storageError } = await supabase.storage.from(bucket).remove([storagePath]);

      if (storageError) {
        logger.error("Erreur lors de la suppression de l'asset du storage:", storageError);
        return { data: null, error: new Error(storageError.message) };
      }

      // Supprimer les métadonnées de la table assets
      const { error: metadataError } = await supabase
        .from('template_editor_assets')
        .delete()
        .eq('storage_path', storagePath);

      if (metadataError) {
        logger.warn("Erreur lors de la suppression des métadonnées de l'asset:", metadataError);
        // Ne pas bloquer si les métadonnées échouent, le fichier est quand même supprimé
      }

      logger.info(`✅ Asset supprimé: ${storagePath}`);
      return { data: null, error: null };
    } catch (error) {
      logger.error("Exception lors de la suppression de l'asset:", error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Liste les assets d'un projet
   */
  async getProjectAssets(projectId: string): Promise<AssetOperationResult<AssetRow[]>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de récupération des assets sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_assets')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la récupération des assets du projet:', error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ ${data?.length || 0} asset(s) récupéré(s) pour le projet ${projectId}`);
      return { data: data as AssetRow[], error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des assets du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Liste tous les assets de l'utilisateur
   */
  async getUserAssets(): Promise<AssetOperationResult<AssetRow[]>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de récupération des assets sans configuration Supabase');
      return { data: null, error };
    }

    try {
      const { data, error } = await supabase
        .from('template_editor_assets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        logger.error("Erreur lors de la récupération des assets de l'utilisateur:", error);
        return { data: null, error: new Error(error.message) };
      }

      logger.info(`✅ ${data?.length || 0} asset(s) récupéré(s)`);
      return { data: data as AssetRow[], error: null };
    } catch (error) {
      logger.error("Exception lors de la récupération des assets de l'utilisateur:", error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * PHASE 2: Retourne le storage path formaté au lieu de public URL
   * Le bucket est privé, donc utilisez StorageUrlService.ensureSignedUrl() pour obtenir une signed URL
   *
   * @deprecated Utilisez StorageUrlService.formatStoragePathAsUrl() et ensureSignedUrl() à la place
   */
  getPublicUrl(storagePath: string, bucket: string = this.DEFAULT_BUCKET): string | null {
    if (!isSupabaseConfigured()) {
      logger.warn("Tentative de récupération d'URL sans configuration Supabase");
      return null;
    }

    // PHASE 2: Retourner storage_path formaté au lieu de public URL
    return formatStoragePathAsUrl(storagePath, bucket);
  }

  /**
   * Upload une thumbnail pour un projet
   */
  async uploadThumbnail(
    projectId: string,
    file: File
  ): Promise<AssetOperationResult<UploadAssetResult>> {
    return this.uploadAsset({
      file,
      projectId,
      bucket: 'thumbnails',
    });
  }

  /**
   * Supprime tous les assets d'un projet
   */
  async deleteProjectAssets(projectId: string): Promise<AssetOperationResult<void>> {
    if (!isSupabaseConfigured() || !supabase) {
      const error = new Error("Supabase n'est pas configuré");
      logger.warn('Tentative de suppression des assets sans configuration Supabase');
      return { data: null, error };
    }

    try {
      // Récupérer tous les assets du projet
      const { data: assets, error: fetchError } = await this.getProjectAssets(projectId);

      if (fetchError || !assets) {
        return { data: null, error: fetchError || new Error('Impossible de récupérer les assets') };
      }

      // Supprimer chaque asset
      const deletePromises = assets.map(asset =>
        this.deleteAsset(asset.storage_path, 'user-uploads')
      );

      await Promise.all(deletePromises);

      logger.info(`✅ Tous les assets du projet ${projectId} ont été supprimés`);
      return { data: null, error: null };
    } catch (error) {
      logger.error('Exception lors de la suppression des assets du projet:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Formate la taille d'un fichier en format lisible
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  /**
   * Valide un fichier avant upload
   */
  validateFile(file: File, maxSizeMB: number = 10): { valid: boolean; error?: string } {
    // Vérifier le type MIME
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Type de fichier non autorisé: ${file.type}. Types acceptés: ${allowedTypes.join(', ')}`,
      };
    }

    // Vérifier la taille
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `Fichier trop volumineux: ${this.formatFileSize(file.size)}. Taille maximale: ${maxSizeMB} MB`,
      };
    }

    return { valid: true };
  }

  /**
   * Compresse une image avant upload (optionnel)
   */
  async compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionner si nécessaire
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Impossible de créer le contexte canvas'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(new Error("Échec de la compression de l'image"));
                return;
              }

              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });

              logger.info(
                `✅ Image compressée: ${this.formatFileSize(file.size)} → ${this.formatFileSize(compressedFile.size)}`
              );
              resolve(compressedFile);
            },
            file.type,
            quality
          );
        };

        img.onerror = () => reject(new Error("Impossible de charger l'image"));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Impossible de lire le fichier'));
      reader.readAsDataURL(file);
    });
  }
}

// Instance singleton
export const assetsService = new AssetsService();
